import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Search, Package, CheckCircle, 
  XCircle, Clock, Save, Phone, Calendar,
  ExternalLink, Copy
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { fetchOrders, updateOrder } from '../../redux/actions/orderActions';
import { parseISO, format } from 'date-fns';

const AdminOrderPanel = () => {
  const dispatch = useDispatch();
  const orders = useSelector((state) => state.orders?.allOrders || []);
  const navigate = useNavigate();
  
  // Estados locales
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState({
    start: '',
    end: ''
  });
  const [editingNotes, setEditingNotes] = useState({});
  const [unsavedNotes, setUnsavedNotes] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentExpenseType, setCurrentExpenseType] = useState('');
  const [currentStatus, setCurrentStatus] = useState('');
  const [currentExpenseAmount, setCurrentExpenseAmount] = useState('');
  const [selectedOrderForExpense, setSelectedOrderForExpense] = useState(null);
  const [orderGkNumber, setOrderGkNumber] = useState('');
  const [epagoName, setEpagoName] = useState('');
  const [internationalShippingNumber, setInternationalShippingNumber] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [selectedOrderForNotes, setSelectedOrderForNotes] = useState(null);

  // Estados posibles de un pedido
  const orderStatuses = {
      'Pago pendiente': { label: 'Pago pendiente', icon: Clock, color: 'text-yellow-800 bg-yellow-100 border border-yellow-300' }, 
      'Pago realizado': { label: 'Pago realizado', icon: CheckCircle, color: 'text-blue-700 bg-blue-100 border border-blue-300' }, 
      'Pago recibido': { label: 'Pago recibido', icon: CheckCircle, color: 'text-green-700 bg-green-100 border border-green-300' },
      'Pedido realizado': { label: 'Pedido realizado', icon: Package, color: 'text-blue-700 bg-blue-100 border border-blue-300' }, // Pedir precio mercaderia + OrderGK
      'Pedido confirmado': { label: 'Pedido confirmado', icon: Package, color: 'text-blue-700 bg-blue-100 border border-blue-300' }, 
      'Paquete en envio internacional': { label: 'Paquete en envio internacional', icon: Package, color: 'text-indigo-700 bg-indigo-100 border border-indigo-300' }, // Pedir numero envio internacional
      'Envio internacional, Epago OK': { label: 'Envio internacional, Epago OK', icon: Package, color: 'text-purple-800 bg-purple-100 border border-purple-300' }, // Pedir cuenta epago
      'Paquete en aduana': { label: 'Paquete en aduana', icon: Package, color: 'text-purple-800 bg-purple-100 border border-purple-300' },
      'Gestion aduanera realizada': { label: 'Gestión aduanera realizada', icon: Package, color: 'text-purple-700 bg-purple-100 border border-purple-300' }, // Pedir precio aduana
      'Paquete en envio a deposito': { label: 'Paquete en envío a depósito', icon: Package, color: 'text-indigo-700 bg-indigo-100 border border-indigo-300' }, 
      'Paquete en deposito': { label: 'Paquete en depósito', icon: Package, color: 'text-indigo-800 bg-indigo-100 border border-indigo-300' },
      'Envio nacional gestionado': { label: 'Envío nacional gestionado', icon: Package, color: 'text-cyan-700 bg-cyan-100 border border-cyan-300' }, // Pedir precio envio + tracking
      'Paquete en envio a destino': { label: 'Paquete en envío a destino', icon: Package, color: 'text-cyan-800 bg-cyan-100 border border-cyan-300' },
      'Pedido entregado': { label: 'Pedido entregado', icon: CheckCircle, color: 'text-green-800 bg-green-100 border border-green-300' },
      'Pedido cancelado': { label: 'Pedido cancelado', icon: XCircle, color: 'text-red-700 bg-red-100 border border-red-300' },
      'Pedido con problemas': { label: 'Pedido con problemas', icon: XCircle, color: 'text-red-800 bg-red-100 border border-red-300' }
  };

  useEffect(() => {
    dispatch(fetchOrders());
  }, [dispatch]);

  // Función auxiliar para manejar fechas
  const getDateFromOrder = (order, field = 'createdAt') => {
    const dateStr = order[field] || order.created_at;
    if (!dateStr) return new Date();
    try {
      return parseISO(dateStr);
    } catch (e) {
      console.error(`Error parsing date (${field}):`, dateStr);
      return new Date(dateStr);
    }
  }

  // Función para formatear fechas de manera consistente
  const formatDate = (date) => {
    try {
      return format(date, 'dd/MM/yyyy HH:mm');
    } catch (e) {
      console.error('Error formatting date:', date);
      return '';
    }
  }

  // Filtrado de órdenes
  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.id.toString().includes(searchTerm) || 
                         (order.shipping_address?.buyerInfo?.nombre || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (order.shipping_address?.buyerInfo?.apellido || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (order.shipping_address?.buyerInfo?.telefono || '').includes(searchTerm);
    
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    
    const orderDate = getDateFromOrder(order);
    const orderDateString = orderDate.toISOString().split('T')[0];
    
    const matchesDateRange = (
        !dateRange.start || orderDateString >= dateRange.start
    ) && (
        !dateRange.end || orderDateString <= dateRange.end
    );

    return matchesSearch && matchesStatus && matchesDateRange;
  });

  const handleRowClick = (orderId) => {
    navigate(`/orders/${orderId}`);
  };

  // Manejador para las notas
  const handleNotesChange = (orderId, notes) => {
    setEditingNotes({
      ...editingNotes,
      [orderId]: notes
    });
    setUnsavedNotes({
      ...unsavedNotes,
      [orderId]: true
    });
  };

  // Manejador para guardar las notas
  const handleSaveNotes = async (orderId) => {
    try {
      await dispatch(updateOrder(orderId, { notes: editingNotes[orderId] }));
      setUnsavedNotes({
        ...unsavedNotes,
        [orderId]: false
      });
      toast.success('Notas guardadas correctamente');
    } catch (error) {
      toast.error('Error al guardar las notas');
    }
  };

  // Abrir modal de notas
  const openNotesModal = (order) => {
    setSelectedOrderForNotes(order);
    setEditingNotes({
      ...editingNotes,
      [order.id]: order.notes || ''
    });
    setIsNotesModalOpen(true);
  };

  // Manejador de cambio de estado
  const handleStatusChange = (e, orderId) => {
    e.stopPropagation();
    const newStatus = e.target.value;
    
    // Buscar la orden actual
    const currentOrder = orders.find(order => order.id === orderId);
    
    // Determinar si necesitamos capturar datos adicionales basados en el nuevo estado
    if (newStatus === 'Pedido realizado') {
      setSelectedOrderForExpense(currentOrder);
      setCurrentExpenseType('mercaderia');
      setCurrentStatus(newStatus);
      setCurrentExpenseAmount('');
      setOrderGkNumber(currentOrder.shipping_address?.orderInfo?.ordergk || '');
      setIsModalOpen(true);
      return;
    }
    
    if (newStatus === 'Paquete en envio internacional') {
      setSelectedOrderForExpense(currentOrder);
      setCurrentExpenseType('');
      setCurrentStatus(newStatus);
      setInternationalShippingNumber(currentOrder.shipping_address?.orderInfo?.enviogk || '');
      setIsModalOpen(true);
      return;
    }

    if (newStatus === 'Envio internacional, Epago OK') {
      setSelectedOrderForExpense(currentOrder);
      setCurrentExpenseType('');
      setCurrentStatus(newStatus);
      setEpagoName(currentOrder.shipping_address?.orderInfo?.epagoName || '');
      setIsModalOpen(true);
      return;
    }
    
    if (newStatus === 'Gestion aduanera realizada') {
      setSelectedOrderForExpense(currentOrder);
      setCurrentExpenseType('aduana');
      setCurrentStatus(newStatus);
      setCurrentExpenseAmount('');
      setIsModalOpen(true);
      return;
    }
    
    if (newStatus === 'Envio nacional gestionado') {
      setSelectedOrderForExpense(currentOrder);
      setCurrentExpenseType('envio');
      setCurrentStatus(newStatus);
      setCurrentExpenseAmount('');
      setTrackingNumber(currentOrder.tracking_number || '');
      setIsModalOpen(true);
      return;
    }
    
    // Si no es un estado que requiera datos adicionales, proceder normalmente
    try {
      dispatch(updateOrder(orderId, { status: newStatus }));
      toast.success('Estado del pedido actualizado');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al actualizar el estado del pedido');
    }
  };

  // Manejar guardado de datos adicionales con el estado
  const handleSaveModalData = async () => {
    if (!selectedOrderForExpense || !currentStatus) return;
    
    try {
      const orderId = selectedOrderForExpense.id;
      const updateData = { status: currentStatus };
      
      // Preparar datos específicos según el tipo de estado
      if (currentExpenseType) {
        // Actualizar gastos
        const currentGastos = selectedOrderForExpense.gastos || {};
        updateData.gastos = {
          ...currentGastos,
          [currentExpenseType]: parseFloat(currentExpenseAmount) || 0
        };
      }
      
      // Actualizar número de orden GK si aplica
      if (currentStatus === 'Pedido realizado' && orderGkNumber) {
        updateData.shipping_address = {
          ...selectedOrderForExpense.shipping_address,
          orderInfo: {
            ...(selectedOrderForExpense.shipping_address?.orderInfo || {}),
            ordergk: orderGkNumber
          }
        };
      }
      
      // Actualizar número de envío internacional si aplica
      if (currentStatus === 'Paquete en envio internacional' && internationalShippingNumber) {
        updateData.shipping_address = {
          ...selectedOrderForExpense.shipping_address,
          orderInfo: {
            ...(selectedOrderForExpense.shipping_address?.orderInfo || {}),
            enviogk: internationalShippingNumber
          }
        };
      }

      // Actualizar nombre epago si aplica
      if (currentStatus === 'Envio internacional, Epago OK') {
        updateData.shipping_address = {
          ...selectedOrderForExpense.shipping_address,
          orderInfo: {
            ...(selectedOrderForExpense.shipping_address?.orderInfo || {}),
            epagoName: epagoName
          }
        };
      }
      
      // Actualizar número de tracking nacional si aplica
      if (currentStatus === 'Envio nacional gestionado' && trackingNumber) {
        updateData.tracking_number = trackingNumber;
      }
      
      // Enviar actualización
      await dispatch(updateOrder(orderId, updateData));
      
      setIsModalOpen(false);
      toast.success('Datos del pedido actualizados correctamente');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al actualizar los datos del pedido');
    }
  };

  // Renderizar el badge de estado
  const StatusBadge = ({ status }) => {
    const StatusIcon = orderStatuses[status]?.icon;
    return (
      <span className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs md:text-sm ${orderStatuses[status]?.color}`}>
        <StatusIcon size={16} />
        <span>{orderStatuses[status].label}</span>
      </span>
    );
  };

  // Función para copiar texto al portapapeles
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Código copiado al portapapeles');
    }, () => {
      toast.error('Error al copiar el código');
    });
  };

  // Botones para tracking de ordergk
  const OrderGkButtons = ({ ordergk }) => {
    if (!ordergk) return null;
    
    const orderNumbers = ordergk.split(' ');
    
    return (
      <div className="flex flex-wrap gap-1">
        {orderNumbers.map((number, index) => (
          <a 
            key={index}
            href={`https://www.grkits3.com/h-account-orderDetail.html?itemno=${number}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs hover:bg-blue-200"
          >
            <ExternalLink size={12} className="mr-1" />
            OrdenGk
          </a>
        ))}
      </div>
    );
  };

  // Botones para tracking internacional
  const EnvioGkButtons = ({ enviogk, epagoName }) => {
    if (!enviogk) return null;
    
    const trackingNumbers = enviogk.split(' ');
    
    return (
      <div className="flex flex-wrap gap-1">
        {trackingNumbers.map((number, index) => (
          <div key={index} className="flex space-x-1">
            <a 
              href={`https://t.17track.net/es#nums=${number}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center px-2 py-1 bg-indigo-100 text-indigo-800 rounded text-xs hover:bg-indigo-200"
            >
              <ExternalLink size={12} className="mr-1" />
              17Track
            </a>
            <a 
              href={`https://epago.correoargentino.com.ar/#/tracing/${number}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 rounded text-xs hover:bg-green-200"
            >
              <ExternalLink size={12} className="mr-1" />
              Epago {epagoName ? `(${epagoName})` : ''}
            </a>
          </div>
        ))}
      </div>
    );
  };

  // Botones para tracking nacional
  const TrackingButtons = ({ trackingNumber }) => {
    if (!trackingNumber) return null;
    
    return (
      <div className="flex space-x-1 items-center">
        <a 
          href="https://www.correoargentino.com.ar/formularios/e-commerce"
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center px-2 py-1 bg-cyan-100 text-cyan-800 rounded text-xs hover:bg-cyan-200"
        >
          <ExternalLink size={12} className="mr-1" />
          Correo
        </a>
        <button
          onClick={(e) => {
            e.stopPropagation();
            copyToClipboard(trackingNumber);
          }}
          className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs hover:bg-gray-200"
          title="Copiar código"
        >
          <Copy size={12} className="mr-1" />
          Copiar
        </button>
      </div>
    );
  };

  // Obtener detalles de seguimiento con botones interactivos
  const getTrackingDetails = (order) => {
    const details = [];
    
    if (order.shipping_address?.orderInfo?.ordergk) {
      details.push({
        label: 'Orden GK',
        content: <OrderGkButtons ordergk={order.shipping_address.orderInfo.ordergk} />
      });
    }
    
    if (order.shipping_address?.orderInfo?.enviogk) {
      details.push({
        label: 'Envío Int.',
        content: <EnvioGkButtons 
                  enviogk={order.shipping_address.orderInfo.enviogk} 
                  epagoName={order.shipping_address?.orderInfo?.epagoName} 
                />
      });
    }
    
    if (order.tracking_number) {
      details.push({
        label: 'Tracking',
        content: <TrackingButtons trackingNumber={order.tracking_number} />
      });
    }
    
    return details;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <h2 className="text-2xl font-bold text-gray-900">Gestión de Pedidos</h2>
        
        {/* Estadísticas rápidas */}
        <div className="flex flex-wrap gap-2 w-full lg:w-auto overflow-x-auto">
          {Object.entries(orderStatuses).map(([key, value]) => (
            <div key={key} className="text-center px-2 min-w-fit">
              <div className={`text-lg font-bold ${value.color} rounded-lg px-2 py-1`}>
                {orders.filter(order => order.status === key).length}
              </div>
              <div className="text-xs text-gray-500 truncate max-w-24">{value.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white p-4 rounded-lg shadow-sm space-y-4">
        <div className="flex flex-col md:flex-row justify-between gap-4">
          {/* Búsqueda */}
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Buscar por ID, cliente o teléfono"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg"
            />
            <Search className="absolute left-3 top-2.5 text-gray-400" size={20} />
          </div>

          {/* Filtro de estado */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border rounded-lg px-4 py-2 w-full md:w-auto"
          >
            <option value="all">Todos los estados</option>
            {Object.entries(orderStatuses).map(([key, { label }]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>

          {/* Filtro de fecha */}
          <div className="flex flex-col md:flex-row items-center gap-2">
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
              className="border rounded-lg px-4 py-2 w-full md:w-auto"
            />
            <span className="hidden md:inline">a</span>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
              className="border rounded-lg px-4 py-2 w-full md:w-auto"
            />
            {(dateRange.start || dateRange.end) && (
              <button
                onClick={() => setDateRange({ start: '', end: '' })}
                className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Limpiar
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Lista de pedidos - Versión responsive */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cliente</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fechas</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Seguimiento</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredOrders.map((order) => (
                <tr 
                  key={order.id} 
                  onClick={() => handleRowClick(order.id)}
                  className="cursor-pointer hover:bg-gray-50"
                >
                  <td className="px-3 py-4 whitespace-nowrap text-sm">#{order.id.slice(0,8)}</td>
                  <td className="px-3 py-4">
                    <div className="text-sm font-medium text-gray-900">
                      {`${order.shipping_address?.buyerInfo?.nombre || ''} ${order.shipping_address?.buyerInfo?.apellido || ''}`}
                    </div>
                    <div className="text-sm text-gray-500 flex items-center">
                      <Phone size={14} className="mr-1" />
                      {order.shipping_address?.buyerInfo?.telefono || 'N/A'}
                    </div>
                  </td>
                  <td className="px-3 py-4 text-xs text-gray-500">
                    <div className="flex flex-col">
                      <div className="flex items-center">
                        <Calendar size={14} className="mr-1" />
                        <span>Creado: {formatDate(getDateFromOrder(order))}</span>
                      </div>
                      {order.updatedAt && (
                        <div className="flex items-center text-xs text-gray-400 mt-1">
                          <Clock size={14} className="mr-1" />
                          <span>Act: {formatDate(getDateFromOrder(order, 'updatedAt'))}</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap">
                    <StatusBadge status={order.status} />
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-xs space-y-2">
                      {getTrackingDetails(order).map((detail, index) => (
                        <div key={index} className="flex flex-col">
                          <span className="font-medium text-gray-700 mb-1">{detail.label}:</span>
                          {detail.content}
                        </div>
                      ))}
                      {getTrackingDetails(order).length === 0 && (
                        <span className="text-gray-400">Sin datos de seguimiento</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                    ${parseFloat(order.total || 0).toFixed(2)}
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm font-medium" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center space-x-2">
                      <select
                        value={order.status || ''}
                        onChange={(e) => handleStatusChange(e, order.id)}
                        className="border rounded-lg px-2 py-1 text-sm"
                      >
                        {Object.entries(orderStatuses).map(([key, { label }]) => (
                          <option key={key} value={key}>{label}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => openNotesModal(order)}
                        className={`p-2 rounded-lg ${unsavedNotes[order.id] ? 'bg-yellow-100 hover:bg-yellow-200' : 'hover:bg-gray-100'}`}
                        title="Ver/editar notas"
                      >
                        <Save size={18} className={unsavedNotes[order.id] ? 'text-yellow-600' : 'text-gray-600'} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal para datos adicionales según estado */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-medium mb-4">
              {currentStatus === 'Pedido realizado' && 'Actualizar Pedido Realizado'}
              {currentStatus === 'Paquete en envio internacional' && 'Actualizar Envío Internacional'}
              {currentStatus === 'Envio internacional, Epago OK' && 'Envio internacional, Epago OK'}
              {currentStatus === 'Gestion aduanera realizada' && 'Actualizar Gestión Aduanera'}
              {currentStatus === 'Envio nacional gestionado' && 'Actualizar Envío Nacional'}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Para el pedido #{selectedOrderForExpense?.id.slice(0,8)}
            </p>
            
            {/* Campos específicos según el estado */}
            {(currentStatus === 'Pedido realizado' || currentStatus === 'Gestion aduanera realizada' || currentStatus === 'Envio nacional gestionado') && currentExpenseType && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Monto de {currentExpenseType === 'mercaderia' ? 'mercadería' : 
                            currentExpenseType === 'aduana' ? 'aduana' : 'envío'}:
                </label>
                <input
                  type="number"
                  value={currentExpenseAmount}
                  onChange={(e) => setCurrentExpenseAmount(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="Ingrese el monto"
                />
              </div>
            )}
            
            {currentStatus === 'Pedido realizado' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Número de orden GK:
                </label>
                <input
                  type="text"
                  value={orderGkNumber}
                  onChange={(e) => setOrderGkNumber(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="Ingrese el número de orden"
                />
              </div>
            )}
            
            {currentStatus === 'Paquete en envio internacional' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Número de envío internacional:
                </label>
                <input
                  type="text"
                  value={internationalShippingNumber}
                  onChange={(e) => setInternationalShippingNumber(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="Ingrese el número de envío internacional"
                />
              </div>
            )}

            {currentStatus === 'Envio internacional, Epago OK' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre para Epago:
                </label>
                <input
                  type="text"
                  value={epagoName}
                  onChange={(e) => setEpagoName(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="Ingrese el nombre para Epago"
                />
              </div>
            )}
            
            {currentStatus === 'Envio nacional gestionado' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Número de tracking:
                </label>
                <input
                  type="text"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="Ingrese el número de tracking"
                />
              </div>
            )}
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveModalData}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para notas */}
      {isNotesModalOpen && selectedOrderForNotes && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">
                Notas para pedido #{selectedOrderForNotes.id.slice(0,8)}
              </h3>
              <button 
                onClick={() => setIsNotesModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <XCircle size={20} />
              </button>
            </div>
            
            <textarea
              value={editingNotes[selectedOrderForNotes.id] || ''}
              onChange={(e) => handleNotesChange(selectedOrderForNotes.id, e.target.value)}
              placeholder="Agregar notas..."
              className="border rounded-lg px-4 py-3 w-full h-56 resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            
            <div className="flex justify-end mt-4">
              <button
                onClick={() => handleSaveNotes(selectedOrderForNotes.id)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
              >
                <Save size={18} className="mr-2" />
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrderPanel;